// Invoices, billing profile, threshold charges, monthly statements.
// Bi-weekly invoices built from the daily series (1st–15th, 16th–end), all
// paid by autopay so the payment form (Stripe) never has to load.
import {
  DEMO_CLIENT, MARKETS, VERTICALS, addDays, dailyRows, iso, round2, today,
} from '../client';
import { territoryId } from './20_budget';

const FEE = DEMO_CLIENT.fee_pct / 100;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmtDay = (d) => `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}`;

function periods(count = 10) {
  // Most recent completed half-month first.
  const out = [];
  const t = today();
  let end;
  if (t.getDate() > 16) end = new Date(t.getFullYear(), t.getMonth(), 15);
  else end = new Date(t.getFullYear(), t.getMonth(), 0);
  for (let i = 0; i < count; i += 1) {
    const start = end.getDate() === 15 ? new Date(end.getFullYear(), end.getMonth(), 1) : new Date(end.getFullYear(), end.getMonth(), 16);
    out.push({ start, end });
    end = end.getDate() === 15 ? new Date(end.getFullYear(), end.getMonth(), 0) : new Date(end.getFullYear(), end.getMonth(), 15);
  }
  return out;
}

let cache = null;
function invoices() {
  if (cache) return cache;
  const rows = dailyRows();
  cache = periods().map(({ start, end }, i) => {
    const s = iso(start);
    const e = iso(end);
    const inPeriod = rows.filter((r) => r.date >= s && r.date <= e);
    const breakdown = [];
    MARKETS.forEach((m) => VERTICALS.forEach((v) => {
      const spend = round2(inPeriod.filter((r) => r.market === m.name && r.vertical === v.name).reduce((a, r) => a + r.spend, 0));
      breakdown.push({
        territory: m.name, territory_id: territoryId(m), vertical: v.name,
        base_spend: spend.toFixed(2), spend: spend.toFixed(2), total_cost: round2(spend * (1 + FEE)).toFixed(2),
      });
    }));
    const spend = round2(breakdown.reduce((a, b) => a + Number(b.base_spend), 0));
    const fee = round2(spend * FEE);
    const total = round2(spend + fee);
    const issue = addDays(end, 2);
    const due = addDays(issue, 7);
    const paidOn = addDays(issue, 1);
    const number = `INV-${String(24180 - i * 7)}`;
    return {
      id: 880000 + i,
      number,
      client_name: DEMO_CLIENT.name,
      parent_company_name: null,
      billing_scope: 'client',
      period_start: s,
      period_end: e,
      period_label: `${fmtDay(start)} – ${fmtDay(end)}, ${end.getFullYear()}`,
      issue_date: iso(issue),
      due_date: iso(due),
      status: 'paid',
      payment_state: 'paid',
      urgency: null,
      days_past_due: null,
      total_amount: total.toFixed(2),
      amount_paid: total.toFixed(2),
      balance_due: '0.00',
      late_strike: false,
      paid_days_late: 0,
      payment_reported: false,
      payment_report_expires_on: null,
      paid_at: `${iso(paidOn)}T14:00:00Z`,
      fee_percentage: DEMO_CLIENT.fee_pct.toFixed(2),
      line_items: [
        { description: 'Advertising spend', display_description: `Advertising spend (${fmtDay(start)} – ${fmtDay(end)})`, fee_percentage: null, line_total: spend.toFixed(2) },
        { description: 'Management fee', display_description: `Management fee (${DEMO_CLIENT.fee_pct}%)`, fee_percentage: DEMO_CLIENT.fee_pct.toFixed(2), line_total: fee.toFixed(2) },
      ],
      territory_breakdown: breakdown,
      territory_breakdown_mode: 'full',
      territory_breakdown_total: total.toFixed(2),
      territory_ad_spend_total: spend.toFixed(2),
      territory_total_cost: total.toFixed(2),
      client_breakdown: [],
      meta_charges: null,
      attachments: [],
      external_notes: 'Thank you for your business! Paid automatically by card on file.',
      billing_from_snapshot: { name: 'Lead Gurus', line1: '', city: '', state: '', postal_code: '', email: 'billing@leadgurus.example' },
      billing_contacts_snapshot: [{ name: DEMO_CLIENT.contact_name, email: DEMO_CLIENT.contact_email }],
      billing_address_snapshot: { line1: '1200 Summit Ridge Pkwy', city: 'Charlotte', state: 'NC', postal_code: '28277' },
      ach_instructions: '',
      autopay_consent: { by_payment_method: { card: { authorized: true } } },
      autopay_consent_text: '',
      payment_failure_message: null,
      payment_failed_at: null,
      payment_options_snapshot: [],
      client_logo_file: null,
    };
  });
  return cache;
}

const paymentMethods = () => ({
  methods: [{
    id: 'pm_demo_visa', type: 'card', label: 'Visa ending 4242', is_default: true, exp_month: 8, exp_year: today().getFullYear() + 2,
    is_verified: true, verification_url: null, last_charge: { succeeded: true, created: Math.floor(addDays(today(), -2).getTime() / 1000), error_message: null },
  }],
  autopay_consent: { authorized: true, methods: ['card'] },
  autopay_consent_text: 'Invoices are charged automatically to the default payment method on their due date.',
  health: { status: 'ok', method_label: 'Visa ending 4242', last_failure_message: null },
  note: null,
});

const PROFILE_ID = 66001;
const billingProfiles = () => [{
  id: PROFILE_ID, client: DEMO_CLIENT.id, client_name: DEMO_CLIENT.name, owner_name: DEMO_CLIENT.name,
  client_billing_ui_enabled: true, payment_method_priority: 'card',
  payment_health: { status: 'ok', method_label: 'Visa ending 4242', last_failure_message: null, card_expires_on: null, budget_gate_enabled: false, blocking: false },
}];

function liveStatus() {
  const t = today();
  const lastCharge = addDays(t, -3);
  const unbilled = round2(dailyRows().filter((r) => r.date > iso(lastCharge)).reduce((a, r) => a + r.spend, 0) * (1 + FEE));
  return {
    state: 'healthy', state_display: 'Healthy',
    unbilled_spend: unbilled.toFixed(2), threshold_amount: '10000.00',
    threshold_progress_pct: Math.min(100, Math.round((unbilled / 10000) * 100)),
    threshold_locked_by_staff: false, threshold_bounds: { min: '2500.00', max: '25000.00' },
    tier_ach_eligible: true, fee_projection: { monthly_savings_on_ach: '412.00' },
    last_threshold_charge_at: `${iso(lastCharge)}T15:02:00Z`, prepaid_balance: null,
  };
}

function thresholdCharges() {
  const out = [];
  for (let i = 0; i < 14; i += 1) {
    const d = addDays(today(), -3 - i * 4);
    out.push({
      id: 77000 + i, triggered_at: `${iso(d)}T15:02:00Z`, cumulative_spend: '10000.00',
      invoice_payment_method: 'Visa ending 4242', outcome: 'charged', outcome_display: 'Charged', stripe_receipt_url: null,
    });
  }
  return out;
}

function statementPeriods() {
  const out = [];
  const t = today();
  for (let i = 1; i <= 12; i += 1) {
    const d = new Date(t.getFullYear(), t.getMonth() - i, 1);
    out.push({ value: iso(d).slice(0, 7), label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` });
  }
  return out;
}

function statementData(params) {
  const period = params.get('period') || statementPeriods()[0].value;
  const rows = dailyRows().filter((r) => r.date.startsWith(period));
  const breakdown = MARKETS.map((m) => {
    const base = round2(rows.filter((r) => r.market === m.name).reduce((a, r) => a + r.spend, 0));
    const fee = round2(base * FEE);
    return { territory: m.name, market: m.dma, base_spend: base.toFixed(2), fee_amount: fee.toFixed(2), total: round2(base + fee).toFixed(2) };
  });
  const base = round2(breakdown.reduce((a, b) => a + Number(b.base_spend), 0));
  const fee = round2(base * FEE);
  const label = statementPeriods().find((p) => p.value === period)?.label || period;
  return {
    client_name: DEMO_CLIENT.name, period_label: label, base_spend: base.toFixed(2), fee_amount: fee.toFixed(2),
    fee_percentage: DEMO_CLIENT.fee_pct.toFixed(2), total: round2(base + fee).toFixed(2), breakdown,
  };
}

const findInvoice = (key) => invoices().find((i) => i.number === key || String(i.id) === key);

export const routes = [
  ['GET', 'dashboard/my-invoices/payment-standing', () => ({ applies: false, strike_count: 0, strike_limit: 3, window_days: 180, strikes: [] })],
  ['GET', 'dashboard/my-invoices/:number/payment-config', () => ({})],
  ['GET', 'dashboard/my-invoices/:number/pdf', () => ({ __status: 404, body: { detail: 'PDFs are not available for the demo account.' } })],
  ['GET', 'dashboard/my-invoices/:number', ({ match }) => findInvoice(match.number) || { __status: 404, body: { detail: 'Not found.' } }],
  ['GET', 'dashboard/my-invoices', ({ params }) => {
    const status = (params.get('status') || '').toLowerCase();
    const rows = invoices().filter((i) => !status || i.status === status);
    return { results: rows, count: rows.length, next: null, previous: null };
  }],
  ['GET', 'dashboard/my-billing-profiles/:id/payment-methods', () => paymentMethods()],
  ['GET', 'dashboard/my-billing-profiles/:id/live-status', () => liveStatus()],
  ['GET', 'dashboard/my-billing-profiles', () => billingProfiles()],
  ['GET', 'dashboard/my-threshold-charges', () => thresholdCharges()],
  ['GET', 'dashboard/my-invoice-recipients', () => ({
    scopes: [{ scope: 'client', id: DEMO_CLIENT.id, name: DEMO_CLIENT.name, covers: [], locked_note: null, recipients: [{ email: DEMO_CLIENT.contact_email, name: DEMO_CLIENT.contact_name, locked: false }, { email: 'ap@summitridge.example', name: 'Accounts Payable', locked: false }] }],
  })],
  ['GET', 'card-vault/mine', () => ({ clients: [] })],
  ['GET', 'dashboard/my-statements/data', ({ params }) => statementData(params)],
  ['GET', /^dashboard\/my-statements\/(pdf|csv)\/?$/, () => ({ __status: 404, body: { detail: 'Downloads are not available for the demo account.' } })],
  ['GET', 'dashboard/my-statements', () => ({ clients: [{ client_id: DEMO_CLIENT.id, client_name: DEMO_CLIENT.name }], periods: statementPeriods() })],
  // Writes the pages read a field back from.
  ['POST', 'dashboard/my-billing-profiles/:id/update-threshold', ({ body }) => ({ threshold_amount: String(body?.threshold_amount ?? '10000.00') })],
];
